import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { BackButton, Button, CardSpinner, ConfirmPopup, Icon } from '~/commonComponents';
import { useNavigationDetector } from '~/hooks';
import { isObjectEqual } from '~/utils/Object';
import { SELECT_DEFAULT_INFO_MDB, editJobInfoMdb } from '../../../../../actions';
import { createLoadingSelector } from '../../../../../api/selectors';
import { getAutoFillInfo } from '../../../../../constants';
import { UserProfession, UserSubProfession } from '../../../../../enums/UserProfession';
import { getShouldOfficeShowEfda } from '../../../../../growthbook';
import history from '../../../../../history';
import { getAllProfessionTypeOptions } from '../../../../../utils/DentistUtils';
import { EnumHelper } from '../../../../../utils/EnumHelper';
import Form, { FORM_FIELDS } from '../../components/Form';
import { ProgressBar } from '../../components/ProgressBar';

export const EditShiftContent = () => {
  const navigate = useNavigate();
  const editedDate = history.location?.state?.editedDate || '';
  const currentJob = useSelector(
    (state) => state.multiday.jobInfo.filter((job) => job.localDate === editedDate)[0],
  );
  const initialBreak = currentJob?.break_time === 0 ? '00' : currentJob?.break_time;
  const defaultOpenCardIndex = history.location?.state?.defaultOpenCardIndex;
  const allProfessions = useSelector((state) => state.user.allProfessions);
  const showEfda = getShouldOfficeShowEfda();
  const allProfessionOptions = getAllProfessionTypeOptions(allProfessions, { showEfda });

  const loadingSelector = createLoadingSelector([SELECT_DEFAULT_INFO_MDB]);
  const isLoading = useSelector((state) => loadingSelector(state));
  const dispatch = useDispatch();

  const getInitialValues = useCallback(() => {
    return {
      officeId: currentJob?.officeId,
      offerStartTime: currentJob?.localStart || null,
      offerEndTime: currentJob?.localEnd || null,
      rate: currentJob?.rate || 0,
      lunch: initialBreak || null,
      isLunchPaid: currentJob?.is_break_paid || false,
      professionType:
        UserSubProfession.getName(currentJob?.subProfession) ??
        UserProfession.getName(currentJob?.profession),
      skills: currentJob?.specialty || 'specialty_General Dentistry',
      procedures: currentJob?.procedures ?? [],
      enableAutoFill: currentJob?.autoFill || false,
      enableAutoFillFavorites: currentJob?.autoFillFavorites || false,
      isAssistedHygiene: currentJob?.isAssistedHygiene,
      professionals: currentJob?.professionals,
    };
  }, [currentJob]);

  const [formValues, setFormValues] = useState(getInitialValues());
  const [confirmationExitModalOpen, setConfirmationExitModalOpen] = useState(false);

  const { allowNavigation, blockNavigation, clearPendingNavigation, navigateToPending } =
    useNavigationDetector({
      when: !isObjectEqual(formValues, getInitialValues()),
      onNavigationAttempt: () => {
        setConfirmationExitModalOpen(true);
        blockNavigation();
      },
    });

  const selectedProfessionCode = allProfessionOptions?.filter(
    (item) => item.name === formValues.professionType,
  )?.[0]?.code;
  const autoFillInfo = useMemo(
    () => getAutoFillInfo(formValues?.professionType, formValues?.skills),
    [formValues?.professionType, formValues?.skills],
  );

  const isDisabled =
    !formValues.offerStartTime ||
    !formValues.offerEndTime ||
    formValues.rate === 0 ||
    !formValues.lunch;

  useEffect(() => {
    if (!defaultOpenCardIndex) {
      window.scrollTo(0, 0);
    }
  }, []);

  const handleSubmit = () => {
    allowNavigation();
    const jobData = {
      officeId: formValues.officeId,
      localStart: formValues.offerStartTime,
      localEnd: formValues.offerEndTime,
      rate: formValues.rate,
      is_break_paid: formValues.isLunchPaid,
      break_time: formValues.lunch,
      // use profession. normalized if sub-profession is included
      profession: UserProfession.getNormalizedCode(selectedProfessionCode),

      // include sub-profession if it's a member of UserSubProfession
      subProfession: EnumHelper.getObjectKeys(UserSubProfession).includes(selectedProfessionCode)
        ? selectedProfessionCode
        : null,
      specialty: formValues.skills || 'specialty_General Dentistry',
      autoFill: autoFillInfo?.show ? formValues.enableAutoFill : false,
      autoFillFavorites: autoFillInfo?.show ? formValues.enableAutoFillFavorites : false,
      procedures: formValues.procedures,
      isAssistedHygiene: formValues.isAssistedHygiene,
      professionals: formValues.professionals,
    };

    dispatch(editJobInfoMdb(currentJob.localDate, jobData));
    navigate(-1);
  };

  if (isLoading) {
    return <CardSpinner />;
  }

  return (
    <div className="component_container">
      <div
        className="floating_container"
        style={{ justifyContent: 'flex-start', flexDirection: 'column', paddingBottom: 120 }}
      >
        <div className="card">
          <div style={{ marginTop: 20, marginLeft: 30 }}>
            <BackButton />
          </div>
          <div className="card-body">
            <ProgressBar step={2} title="Edit Shift" isHidden />
            <Form
              job={currentJob}
              formValues={formValues}
              setFormValues={setFormValues}
              defaultOpenCardIndex={defaultOpenCardIndex}
              editDate
              disabledFields={[FORM_FIELDS.DATES, FORM_FIELDS.PROFESSIONAL]}
            />
          </div>
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
            marginTop: 20,
          }}
        >
          <Button
            text="Save Changes"
            onClick={handleSubmit}
            disabled={isDisabled}
            data-testid="save-changes-button"
            dataCy="save-changes-button"
          />
        </div>
      </div>

      {confirmationExitModalOpen && (
        // @ts-expect-error - ConfirmPopup is not typed
        <ConfirmPopup
          Icon={() => <Icon name="warning-outline" size={80} />}
          leftButtonAction={() => {
            clearPendingNavigation();
            setConfirmationExitModalOpen(false);
          }}
          leftButtonText="Go Back"
          rightButtonAction={navigateToPending}
          rightButtonText="Leave Anyway"
          title="Are You Sure You Want to Leave?"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="You've made changes to your shift request that haven't been saved. Do you still want to leave?"
        />
      )}
    </div>
  );
};
